/**
 * Dashboard shared building blocks — Benevita Polar visual language.
 * Extracted from the original Dashboard.js so every role dashboard
 * (Admin, Patient, Nurse, Doctor, Finance, QA, Partner) shares one
 * set of tokens, cards, icons and animation helpers.
 */
import React, { useEffect, useState, useRef } from 'react';

// ═══ Benevita Polar design tokens (mirrors components/ss/tokens BDS palette) ══
export const T = {
  primary:      '#7B1E3C',
  primaryLight: '#94335A',
  sky:          '#C39A4E',
  teal:         '#00897B',
  navy:         '#2B1B22',
  danger:       '#D32F2F',
  warning:      '#F57C00',
  info:         '#0277BD',
  purple:       '#5C35B5',
  green:        '#2E7D32',
  text:         '#2B1B22',
  muted:        '#8b7681',
  border:       '#ECE3E7',
  pageBg:       '#f7f3f4',
};
export const DONUT_COLORS = [T.primary, T.teal, T.sky, T.purple, T.warning, T.info];
export const FONT = "'Inter', sans-serif";
export const CARD = {
  background: '#fff',
  borderRadius: 16,
  border: `1px solid ${T.border}`,
  boxShadow: '0 1px 2px rgba(45,16,32,.04), 0 10px 30px -16px rgba(45,16,32,.10)',
};
export const HERO_GRADIENT = 'linear-gradient(135deg,#2B1B22 0%,#7B1E3C 70%,#94335A 100%)';

// ═══ Inline SVG icons (Lucide-style, no external deps) ════════════════════════
export const Svg = ({ children, size = 16, color, ...rest }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color || 'currentColor'} strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" {...rest}
  >
    {children}
  </svg>
);
export const I = {
  bolt:      (p) => <Svg {...p}><path d="M13 2 3 14h7l-1 8 11-13h-7z" /></Svg>,
  heart:     (p) => <Svg {...p}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></Svg>,
  cross:     (p) => <Svg {...p}><path d="M11 2v6H5v4h6v10h2V12h6V8h-6V2z" /></Svg>,
  calendar:  (p) => <Svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8"  y1="2" x2="8"  y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>,
  stetho:    (p) => <Svg {...p}><path d="M11 2v2H9a3 3 0 0 0-3 3v5a4 4 0 0 0 8 0V7a3 3 0 0 0-3-3h-2" /><path d="M14 14a4 4 0 0 0 8 0v-2" /><circle cx="20" cy="10" r="2" /></Svg>,
  check:     (p) => <Svg {...p}><polyline points="20 6 9 17 4 12" /></Svg>,
  coin:      (p) => <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M9 9h4a2 2 0 1 1 0 4H9zM9 9v8" /><path d="M9 13h5" /></Svg>,
  diamond:   (p) => <Svg {...p}><path d="M2.7 10.3 12 2l9.3 8.3L12 22z" /><path d="M2.7 10.3h18.6" /></Svg>,
  refresh:   (p) => <Svg {...p}><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><polyline points="21 3 21 8 16 8" /></Svg>,
  shield:    (p) => <Svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Svg>,
  pulse:     (p) => <Svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></Svg>,
  user_plus: (p) => <Svg {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></Svg>,
  nurse:     (p) => <Svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /><path d="M10 6h4" /><path d="M12 4v4" /></Svg>,
  request:   (p) => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></Svg>,
  refer:     (p) => <Svg {...p}><line x1="22" y1="2"  x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></Svg>,
  dispatch:  (p) => <Svg {...p}><path d="M3 12h13l-3-3" /><path d="M16 12l-3 3" /><circle cx="20" cy="12" r="2" /></Svg>,
  chart:     (p) => <Svg {...p}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6"  y1="20" x2="6"  y2="14" /></Svg>,
  filter:    (p) => <Svg {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></Svg>,
  x:         (p) => <Svg {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6"  y1="6" x2="18" y2="18" /></Svg>,
  spark:     (p) => <Svg {...p}><path d="M12 2 14 9 21 11 14 13 12 20 10 13 3 11 10 9z" /></Svg>,
  alert:     (p) => <Svg {...p}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></Svg>,
  arrow:     (p) => <Svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></Svg>,
  pill:      (p) => <Svg {...p}><rect x="2" y="8" width="20" height="8" rx="4" transform="rotate(-45 12 12)" /><line x1="8.5" y1="8.5" x2="15.5" y2="15.5" /></Svg>,
  clipboard: (p) => <Svg {...p}><rect x="6" y="4" width="12" height="18" rx="2" /><path d="M9 4V2h6v2" /><line x1="9" y1="10" x2="15" y2="10" /><line x1="9" y1="14" x2="15" y2="14" /></Svg>,
  users:     (p) => <Svg {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Svg>,
  building:  (p) => <Svg {...p}><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="9" y1="7" x2="10" y2="7" /><line x1="14" y1="7" x2="15" y2="7" /><line x1="9" y1="11" x2="10" y2="11" /><line x1="14" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="10" y2="15" /><line x1="14" y1="15" x2="15" y2="15" /><path d="M10 22v-4h4v4" /></Svg>,
  file:      (p) => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></Svg>,
  star:      (p) => <Svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></Svg>,
  clock:     (p) => <Svg {...p}><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></Svg>,
};

// ═══ Small utilities ═══════════════════════════════════════════════════════════
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Animated count-up hook: rAF from 0 over ~700ms, respects reduced motion.
export function useCountUp(target, duration = 700) {
  const numeric = Number(target) || 0;
  const [value, setValue] = useState(prefersReducedMotion() ? numeric : 0);
  const rafRef = useRef();
  useEffect(() => {
    if (prefersReducedMotion()) { setValue(numeric); return; }
    let start;
    const step = (ts) => {
      if (start === undefined) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(numeric * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [numeric, duration]);
  return value;
}

export const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

export const timeOfDay = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
};

export const firstNameOf = (user) =>
  (user?.name || '').trim().split(/\s+/)[0] || 'there';

// Role precedence used by the /dashboard router.
export const ROLE_PRECEDENCE = ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING', 'QA', 'PATIENT', 'ORGANIZATION'];
export function primaryRole(roles) {
  const list = Array.isArray(roles) ? roles : [];
  for (const r of ROLE_PRECEDENCE) if (list.includes(r)) return r;
  return list[0] || 'PATIENT';
}

// Unwrap the various list shapes the API returns into a plain array.
export const asList = (resp) => {
  const d = resp?.data ?? resp;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.results)) return d.results;
  return [];
};

export const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
export const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};
export const fmtMoney = (v) => `KES ${Number(v || 0).toLocaleString()}`;

// ═══ Custom Recharts tooltip ═══════════════════════════════════════════════════
export function ChartTip({ active, payload, label, labelFormatter, nameFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: `1px solid ${T.border}`,
      boxShadow: '0 12px 32px -12px rgba(45,16,32,.25)',
      padding: '10px 14px', fontSize: 12, fontFamily: FONT, color: T.text,
    }}>
      {label !== undefined && (
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, fontWeight: 600 }}>
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: i > 0 ? 4 : 0 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: p.payload?.fill || p.color || p.stroke || T.primary,
          }} />
          <span style={{ color: T.muted }}>
            {nameFormatter ? nameFormatter(p.name) : p.name}
          </span>
          <span style={{ fontWeight: 700, marginLeft: 'auto', paddingLeft: 10 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ═══ Section wrapper with staggered entrance animation ════════════════════════
export function FadeSection({ index = 0, children, style }) {
  return (
    <div
      className="hhms-fade-in bnv-fade"
      style={{ animationDelay: `${Math.min(180 + index * 60, 300)}ms`, ...style }}
    >
      {children}
    </div>
  );
}

// ═══ Hero glassy mini-stat ════════════════════════════════════════════════════
export function HeroStat({ label, value, Icon }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: 'rgba(255,255,255,.12)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      borderRadius: 12, padding: '10px 14px',
      border: '1px solid rgba(255,255,255,.14)',
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 9, flexShrink: 0,
        background: 'rgba(255,255,255,.14)',
      }}>
        <Icon size={15} color="#BEE3FF" />
      </span>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.72)', letterSpacing: '.4px', textTransform: 'uppercase' }}>
          {label}
        </div>
      </div>
    </div>
  );
}

// ═══ KPI card ═════════════════════════════════════════════════════════════════
export function KpiCard({ title, value, displayValue, color, Icon, sub, delta, deltaDir, action }) {
  const counted = useCountUp(value);
  const shown = displayValue !== undefined
    ? (prefersReducedMotion() ? displayValue : Number(counted).toLocaleString())
    : counted;
  const deltaColor = deltaDir === 'up' ? T.green : deltaDir === 'down' ? T.danger : T.muted;
  return (
    <div
      className="bnv-kpi"
      style={{ ...CARD, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44, borderRadius: 12, background: `${color}1A`,
        }}>
          <Icon size={20} color={color} />
        </span>
        {delta && (
          <span style={{
            fontSize: 10.5, fontWeight: 700, color: deltaColor,
            background: `${deltaColor}14`, padding: '3px 9px', borderRadius: 999,
            whiteSpace: 'nowrap',
          }}>
            {deltaDir === 'up' ? '▲' : deltaDir === 'down' ? '▼' : '—'} {delta}
          </span>
        )}
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: T.text, lineHeight: 1.05, letterSpacing: '-.5px' }}>
          {shown}
        </div>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.muted,
          textTransform: 'uppercase', letterSpacing: '.7px', marginTop: 5,
        }}>
          {title}
        </div>
        {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{sub}</div>}
      </div>
      {action && <div style={{ marginTop: 'auto' }}>{action}</div>}
    </div>
  );
}

// ═══ Action link ══════════════════════════════════════════════════════════════
export function ActionLink({ label, onClick, color = T.primary }) {
  return (
    <button
      onClick={onClick}
      className="bnv-action-link"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 12, fontWeight: 700, color, fontFamily: FONT, whiteSpace: 'nowrap',
      }}
    >
      {label} <I.arrow size={12} color={color} />
    </button>
  );
}

// ═══ Urgency alert card (left-accent) ═════════════════════════════════════════
export function AlertCard({ tier, message, actionLabel, onAction }) {
  const map = {
    critical: { color: T.danger,  Icon: I.alert },
    warning:  { color: T.warning, Icon: I.alert },
    info:     { color: T.info,    Icon: I.refer },
  };
  const { color, Icon } = map[tier] || map.info;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      borderLeft: `4px solid ${color}`,
      background: `${color}0F`,
      border: `1px solid ${color}26`,
      borderLeftWidth: 4, borderLeftColor: color, borderLeftStyle: 'solid',
      borderRadius: 12, padding: '12px 16px',
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 10, background: `${color}1A`, flexShrink: 0,
      }}>
        <Icon size={15} color={color} />
      </span>
      <div style={{ fontSize: 12.5, color: T.text, fontWeight: 500, lineHeight: 1.45, flex: 1 }}>
        {message}
      </div>
      <ActionLink label={actionLabel} onClick={onAction} color={color} />
    </div>
  );
}

// ═══ Chart / content card wrapper ═════════════════════════════════════════════
export function ChartCard({ title, subtitle, children, action }) {
  return (
    <div style={{ ...CARD, padding: '20px 22px', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, letterSpacing: '-.1px' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{subtitle}</div>}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
      {children}
    </div>
  );
}

// ═══ Empty-state placeholder ══════════════════════════════════════════════════
export function ChartEmpty({ color, Icon, title, caption, height = 230 }) {
  return (
    <div style={{
      height, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', gap: 10,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 16, background: `${color}12`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={26} color={color} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</div>
      <div style={{ fontSize: 11, color: T.muted, maxWidth: 260 }}>{caption}</div>
    </div>
  );
}

// Compact empty state for list cards.
export function EmptyState({ icon: Icon = I.spark, color = T.primary, title, caption }) {
  return <ChartEmpty color={color} Icon={Icon} title={title} caption={caption} height={150} />;
}

// ═══ Status badge (Polar pill) ════════════════════════════════════════════════
const STATUS_COLORS = {
  SCHEDULED: T.info, IN_PROGRESS: T.primary, EN_ROUTE: T.sky, ARRIVED: T.teal,
  COMPLETED: T.green, CANCELLED: T.danger, NO_SHOW: T.warning, MISSED: T.warning,
  PENDING: T.warning, ACCEPTED: T.teal, REJECTED: T.danger, EXPIRED: T.muted,
  ACTIVE: T.green, DRAFT: T.muted, ON_HOLD: T.warning, REVOKED: T.danger,
  PAID: T.green, OVERDUE: T.danger, PARTIALLY_PAID: T.warning, REFUNDED: T.purple,
  INSURANCE_PROCESSING: T.info, VOID: T.muted, ISSUED: T.info,
};
export function StatusBadge({ value }) {
  const key = String(value || '').toUpperCase();
  const color = STATUS_COLORS[key] || T.muted;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 10, fontWeight: 700, color,
      background: `${color}14`, padding: '3px 9px', borderRadius: 999,
      letterSpacing: '.4px', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
      {key.replace(/_/g, ' ') || '—'}
    </span>
  );
}

// "Planned" pill for honest not-yet-built modules.
export function PlannedPill({ label = 'Planned' }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 10, fontWeight: 700, color: T.purple,
      background: `${T.purple}12`, border: `1px solid ${T.purple}30`,
      padding: '3px 10px', borderRadius: 999, letterSpacing: '.3px', whiteSpace: 'nowrap',
    }}>
      <I.clock size={10} color={T.purple} /> {label}
    </span>
  );
}

// Gradient avatar (initials) used in care-team / people lists.
const AVATAR_GRADS = [
  'linear-gradient(135deg,#7B1E3C,#C39A4E)',
  'linear-gradient(135deg,#00897B,#4DD0C4)',
  'linear-gradient(135deg,#5C35B5,#9C7BE8)',
  'linear-gradient(135deg,#F57C00,#FFB74D)',
  'linear-gradient(135deg,#0277BD,#C39A4E)',
];
export function Avatar({ name, index = 0, size = 36 }) {
  const initials = (name || '?').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: AVATAR_GRADS[index % AVATAR_GRADS.length],
      color: '#fff', fontSize: size * 0.34, fontWeight: 800, letterSpacing: '.5px',
    }}>
      {initials}
    </span>
  );
}

// Row inside a list card.
export function ListRow({ left, title, sub, right }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 0', borderBottom: `1px solid ${T.border}`,
    }}>
      {left}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </div>
        {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{sub}</div>}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ═══ Role hero band ═══════════════════════════════════════════════════════════
export function Hero({ index = 0, title, subtitle, roleLabel, stats, onRefresh }) {
  return (
    <FadeSection index={index}>
      <div style={{
        position: 'relative', overflow: 'hidden',
        marginBottom: 18, padding: '26px 28px',
        background: HERO_GRADIENT,
        borderRadius: 20, color: '#fff',
        boxShadow: '0 14px 36px -14px rgba(45,16,32,.5)',
      }}>
        <div aria-hidden style={{
          position: 'absolute', top: -120, right: -90,
          width: 340, height: 340, borderRadius: '50%',
          background: `radial-gradient(circle, ${T.sky}33 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div aria-hidden style={{
          position: 'absolute', bottom: -140, left: '32%',
          width: 320, height: 320, borderRadius: '50%',
          background: `radial-gradient(circle, ${T.teal}33 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'relative', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          gap: 20, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(217,140,179,.14)',
              border: '1px solid rgba(217,140,179,.3)',
              fontSize: 10.5, fontWeight: 600, color: '#7DD3FC',
              marginBottom: 10, letterSpacing: '.3px',
            }}>
              <I.spark size={11} color="#7DD3FC" />
              {roleLabel}
              <span style={{ opacity: .55 }}>·</span>
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-.5px', color: '#fff' }}>
              {title}
            </h1>
            <p style={{ color: 'rgba(255,255,255,.66)', margin: 0, fontSize: 13, lineHeight: 1.5 }}>
              {subtitle}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {(stats || []).map((s, i) => <HeroStat key={i} {...s} />)}
            {onRefresh && (
              <button
                onClick={onRefresh}
                title="Refresh dashboard data"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40,
                  background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)',
                  borderRadius: 12, color: '#fff', cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.12)'; }}
              >
                <I.refresh size={15} color="#fff" />
              </button>
            )}
          </div>
        </div>
      </div>
    </FadeSection>
  );
}

// ═══ Quick-actions card ═══════════════════════════════════════════════════════
export function QuickActions({ actions, navigate, title = 'Quick Actions' }) {
  return (
    <div style={{ ...CARD, padding: '20px 22px' }}>
      <div style={{
        fontSize: 14, fontWeight: 700, color: T.text,
        marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <I.bolt size={14} color={T.primary} /> {title}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 10,
      }}>
        {actions.map(({ label, path, color, Icon }) => (
          <button
            key={path + label}
            className="bnv-tile"
            onClick={() => navigate(path)}
            style={{
              display: 'flex', alignItems: 'center', gap: 11,
              padding: '13px 15px',
              background: '#fff', color: T.text,
              border: `1px solid ${T.border}`,
              borderRadius: 14, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: FONT,
              textAlign: 'left', justifyContent: 'flex-start',
            }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, borderRadius: 11,
              background: `${color}14`, flexShrink: 0,
            }}>
              <Icon size={18} color={color} />
            </span>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Shared animation CSS (inject once per dashboard).
export function PolarStyles() {
  return (
    <style>{`
      @keyframes hhms-status-pulse {
        0%, 100% { opacity: 1;    transform: scale(1); }
        50%      { opacity: 0.55; transform: scale(0.85); }
      }
      .hhms-status-dot { animation: hhms-status-pulse 2.6s ease-in-out infinite; }

      @keyframes bnv-fade-up {
        from { opacity: 0; transform: translateY(14px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .bnv-fade {
        opacity: 0;
        animation: bnv-fade-up .55s cubic-bezier(.22,.9,.32,1) forwards;
      }
      .bnv-kpi {
        transition: transform .18s ease, box-shadow .18s ease;
      }
      .bnv-kpi:hover {
        transform: translateY(-3px);
        box-shadow: 0 2px 4px rgba(45,16,32,.05), 0 18px 40px -16px rgba(45,16,32,.18);
      }
      .bnv-tile {
        transition: transform .15s ease, border-color .15s ease, background .15s ease, box-shadow .15s ease;
      }
      .bnv-tile:hover {
        border-color: ${T.primary} !important;
        background: #f7f3f4 !important;
        transform: translateY(-2px);
        box-shadow: 0 10px 24px -14px rgba(123,30,60,.35);
      }
      .bnv-action-link { transition: opacity .15s; }
      .bnv-action-link:hover { opacity: .75; }
      @media (prefers-reduced-motion: reduce) {
        .bnv-fade { animation: none; opacity: 1; }
        .hhms-status-dot { animation: none; }
        .bnv-kpi, .bnv-tile { transition: none; }
        .bnv-kpi:hover, .bnv-tile:hover { transform: none; }
      }
    `}</style>
  );
}
